/**
 * TDBLiveMTDPnL — Live Month-to-Date P&L tile.
 *
 * Runs the doctrine engine on `tdb_sales_daily` + Shopify + `mis_transactions`
 * in real time. Tells you the month-to-date P&L at any moment without
 * waiting for Mickky's monthly Excel.
 *
 * Honours the same `localStorage` doctrine choice as <TDBPnLBreakdown />
 * so the two tiles stay in sync.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Activity, TrendingUp, TrendingDown, RefreshCw, AlertTriangle, Zap,
} from 'lucide-react';
import api from '@/lib/api';

const CITY_ORDER = ['Bengaluru', 'Mumbai', 'Gurgaon'];
const BUCKET_ORDER = ['food_cost', 'manpower', 'utilities', 'operating', 'marketing', 'admin_gateway', 'inter_branch', 'capex'];

function thisMonthYM() {
  const ist = new Date(Date.now() + 5.5 * 3600 * 1000);
  return ist.toISOString().slice(0, 7);
}

function fmtRupees(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '₹0';
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function fmtPct(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
}

const COLORS = {
  red:   { bg: '#FEF2F2', fg: '#991B1B' },
  amber: { bg: '#FFFBEB', fg: '#78350F' },
  green: { bg: '#ECFDF5', fg: '#14532D' },
  grey:  { bg: '#F7F5F2', fg: '#6B7280' },
};

function ragForGop(pct) {
  if (pct >= 10) return 'green';
  if (pct >= 0) return 'amber';
  return 'red';
}

export default function TDBLiveMTDPnL({ className = '' }) {
  const [monthYM, setMonthYM] = useState(thisMonthYM());
  const [doctrine, setDoctrine] = useState(() => {
    try {
      return window.localStorage.getItem('tdb_pnl_doctrine') === 'source' ? 'source' : 'scaleboard';
    } catch { return 'scaleboard'; }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await api.get('/tdb-mis/live-mtd-pnl', {
        params: { month_ym: monthYM, doctrine },
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load live P&L');
    } finally {
      setLoading(false);
    }
  }, [monthYM, doctrine]);

  useEffect(() => { load(); }, [load]);

  // Re-read doctrine from localStorage when window regains focus
  // (so toggling on the <TDBPnLBreakdown /> tile updates this one too)
  useEffect(() => {
    const handler = () => {
      try {
        const next = window.localStorage.getItem('tdb_pnl_doctrine');
        if (next && next !== doctrine) setDoctrine(next);
      } catch { /* noop */ }
    };
    window.addEventListener('focus', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('focus', handler);
      window.removeEventListener('storage', handler);
    };
  }, [doctrine]);

  // Auto-refresh every 60s
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  const gt = data?.group_totals || {};
  const byCity = data?.by_city || {};
  const anomalies = data?.anomalies || [];
  const counters = data?.counters || {};
  const gopRag = ragForGop(gt.gop_pct || 0);
  const isSource = data?.doctrine === 'source';

  return (
    <div
      data-testid="tdb-live-mtd-pnl"
      className={`bg-gradient-to-br from-white to-emerald-50/20 rounded-2xl border border-emerald-200 p-6 ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-700" />
          <h3 className="text-base font-semibold text-gray-900">Live MTD P&amp;L</h3>
          <span
            data-testid="tdb-live-mtd-source-pill"
            className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 font-medium animate-pulse"
            title="Computed in real-time from tdb_sales_daily + Shopify + mis_transactions"
          >
            <Zap className="w-3 h-3 inline mr-0.5" /> LIVE · {data?.mtd_end || '—'}
          </span>
          <span
            data-testid="tdb-live-mtd-doctrine-badge"
            className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
              isSource ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'
            }`}
            title={data?.doctrine_note || ''}
          >
            {isSource ? '📜 Source' : '📐 ScaleBoard'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-gray-600 flex items-center gap-1 cursor-pointer">
            <input
              data-testid="tdb-live-mtd-autorefresh"
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-3 h-3"
            />
            auto 60s
          </label>
          <input
            data-testid="tdb-live-mtd-month-picker"
            type="month"
            value={monthYM}
            max={thisMonthYM()}
            onChange={(e) => setMonthYM(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white"
          />
          <button
            data-testid="tdb-live-mtd-refresh"
            onClick={load}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {err && (
        <div data-testid="tdb-live-mtd-error" className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
          {err}
        </div>
      )}

      {/* Group MTD card */}
      <div
        data-testid="tdb-live-mtd-group-card"
        className="mb-3 p-4 rounded-xl border"
        style={{ backgroundColor: COLORS[gopRag].bg, borderColor: COLORS[gopRag].fg + '30' }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-600 font-medium">
              {monthYM} MTD · Group GOP
            </div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="text-3xl font-bold" style={{ color: COLORS[gopRag].fg }}>
                {fmtRupees(gt.gop || 0)}
              </div>
              <div className="text-sm font-medium flex items-center gap-1" style={{ color: COLORS[gopRag].fg }}>
                {(gt.gop_pct || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {fmtPct(gt.gop_pct)}
              </div>
            </div>
          </div>
          <div className="text-right text-xs text-gray-700">
            <div>Income: <span className="font-semibold">{fmtRupees(gt.income || 0)}</span></div>
            <div>Expense: <span className="font-semibold">{fmtRupees(gt.expense || 0)}</span></div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {counters.transactions || 0} txns · {counters.sales_rows || 0} sales · {counters.days_with_entries || 0}d of entries
            </div>
          </div>
        </div>
      </div>

      {/* Per-city strip */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {CITY_ORDER.map((city) => {
          const c = byCity[city] || {};
          const r = ragForGop(c.gop_pct || 0);
          return (
            <div
              key={city}
              data-testid={`tdb-live-mtd-city-${city.toLowerCase()}`}
              className="p-2 rounded-md border"
              style={{ backgroundColor: COLORS[r].bg, borderColor: COLORS[r].fg + '30' }}
            >
              <div className="text-[11px] font-medium text-gray-700">{city}</div>
              <div className="text-base font-semibold" style={{ color: COLORS[r].fg }}>
                {fmtRupees(c.gop || 0)}
              </div>
              <div className="text-[10px]" style={{ color: COLORS[r].fg }}>
                {fmtPct(c.gop_pct)} · inc {fmtRupees(c.grand_total_income || 0)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <div
          data-testid="tdb-live-mtd-anomalies"
          className="mb-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900"
        >
          <div className="flex items-center gap-1 font-semibold mb-1">
            <AlertTriangle className="w-3 h-3" /> Anomalies · {anomalies.length}
          </div>
          <ul className="space-y-0.5">
            {anomalies.slice(0, 4).map((a, i) => (
              <li key={i} data-testid={`tdb-live-mtd-anomaly-${i}`}>• {a.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Bucket matrix · compact 3 cities + group */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="tdb-live-mtd-matrix">
          <thead>
            <tr className="text-[10px] text-gray-500 uppercase tracking-wider">
              <th className="text-left py-1.5 px-1.5 font-medium">Bucket</th>
              {CITY_ORDER.map((c) => (
                <th key={c} className="text-right py-1.5 px-1.5 font-medium">{c.slice(0, 3)}</th>
              ))}
              <th className="text-right py-1.5 px-1.5 font-medium bg-gray-50">Group</th>
            </tr>
          </thead>
          <tbody>
            {BUCKET_ORDER.map((bk) => {
              const gb = (gt.buckets || {})[bk];
              if (!gb || gb.total === 0) return null;
              return (
                <tr
                  key={bk}
                  data-testid={`tdb-live-mtd-row-${bk}`}
                  className="border-t border-gray-100"
                >
                  <td className="py-1 px-1.5 font-medium text-gray-800">
                    <span className="mr-1">{gb.icon}</span>{gb.label}
                  </td>
                  {CITY_ORDER.map((city) => {
                    const cb = ((byCity[city] || {}).buckets || {})[bk] || { total: 0, pct_of_income: 0 };
                    return (
                      <td key={city} className="text-right py-1 px-1.5">
                        <div className="text-gray-900">{fmtRupees(cb.total)}</div>
                        <div className="text-[9px] text-gray-500">{fmtPct(cb.pct_of_income)}</div>
                      </td>
                    );
                  })}
                  <td className="text-right py-1 px-1.5 bg-gray-50 font-semibold">
                    <div className="text-gray-900">{fmtRupees(gb.total)}</div>
                    <div className="text-[9px] text-gray-500">{fmtPct(gb.pct_of_income)}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-[10px] text-gray-500 flex items-center gap-1">
        <Zap className="w-3 h-3 text-emerald-600" />
        Computed in real-time · doctrine syncs with True P&amp;L tile · {data?.computed_at?.slice(11, 16) || '—'} UTC
      </div>
    </div>
  );
}
